import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { CALLABLE_BEER_ADD_BEER, functions } from '../utils/firebase';

const REVIEW_DUPLICATE_CALLABLE = 'callableBeerReviewDuplicate-reviewDuplicate';
const REVIEW_DUPLICATE_TIMEOUT_MS = 300000;

function messageForReviewDuplicateError(code, errMessage) {
  switch (code) {
    case 'unauthenticated':
      return 'Request is not authenticated.';
    case 'invalid-argument':
      return errMessage && errMessage.toLowerCase().includes('merge')
        ? 'No description or ABV to merge from duplicate.'
        : 'Invalid request.';
    case 'not-found':
      return 'Review or matched beer not found.';
    case 'already-exists':
      return 'A beer with this name already exists for the brewery.';
    case 'failed-precondition':
      return 'This duplicate review has already been reviewed.';
    case 'internal':
      return 'An unexpected error occurred.';
    default:
      return errMessage || 'An error occurred.';
  }
}

/**
 * useBeerDuplicateReviewActions
 * Provides actions to complete a duplicate review via the reviewDuplicate callable
 * (Confirmed Duplicate or Updated Original), add the new beer via addBeer callable,
 * or merge duplicate description/ABV into the matched beer (Updated Original).
 *
 * @returns {{
 *   completeReview: (row: { id: string }) => Promise<{ success: boolean, error?: string }>,
 *   addBeerAndCompleteReview: (row: object) => Promise<{ success: boolean, beerId?: string, error?: string }>,
 *   copyDescriptionAndCompleteReview: (row: object) => Promise<{ success: boolean, error?: string }>,
 *   applySelectedChanges: (row: object, selectedFields: string[]) => Promise<{ success: boolean, matchedBeer?: object, error?: string }>,
 *   loading: boolean,
 *   errorMessage: string,
 *   clearError: () => void,
 * }}
 */
function useBeerDuplicateReviewActions() {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  /**
   * Mark the duplicate as reviewed with outcome "Confirmed Duplicate" (no beer changes).
   * Calls the reviewDuplicate callable.
   *
   * @param {object} row - The duplicate review row (must have id).
   * @returns {Promise<{ success: boolean, error?: string }>}
   */
  const completeReview = useCallback(async (row) => {
    if (!row?.id) {
      return { success: false, error: 'Invalid review record' };
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const reviewDuplicateFn = httpsCallable(
        functions,
        REVIEW_DUPLICATE_CALLABLE,
        { timeout: REVIEW_DUPLICATE_TIMEOUT_MS },
      );
      await reviewDuplicateFn({
        reviewId: row.id,
        reviewOutcome: 'Confirmed Duplicate',
      });
      return { success: true };
    } catch (err) {
      console.error('Error completing review:', err);
      const message = messageForReviewDuplicateError(
        err?.code,
        err?.message,
      );
      setErrorMessage(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Add the new beer via addBeer callable (force-add) and mark the duplicate review
   * as reviewed with outcome "Added Beer". Backend creates the beer and updates the review.
   *
   * @param {object} row - The duplicate review row (must have id; breweryId from newBeer or row).
   * @returns {Promise<{ success: boolean, beerId?: string, error?: string }>}
   */
  const addBeerAndCompleteReview = useCallback(async (row) => {
    if (!row?.id) {
      return { success: false, error: 'Invalid review record' };
    }

    const newBeer = row.newBeer || {};
    const breweryId = newBeer.breweryId || row.breweryId;

    if (!breweryId) {
      return { success: false, error: 'Missing brewery ID' };
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const addBeerFn = httpsCallable(
        functions,
        CALLABLE_BEER_ADD_BEER,
        { timeout: 600000 },
      );
      const authUser = getAuth().currentUser;
      const result = await addBeerFn({
        breweryId,
        beerDuplicateReviewId: row.id,
        forceAddDuplicate: true,
        ...(authUser?.uid ? { source: authUser.uid } : {}),
        sourceType: 'dupe_override',
      });

      const data = result?.data ?? {};
      const addedBeers = Array.isArray(data.addedBeers) ? data.addedBeers : [];
      const beerId = addedBeers[0]?.id ?? null;

      return { success: true, beerId };
    } catch (err) {
      console.error('Error adding beer and completing review:', err);
      const code = err?.code;
      let message = err?.message || 'Failed to add beer and complete review';
      if (code === 'failed-precondition') {
        message = 'This duplicate review has already been reviewed.';
      } else if (code === 'already-exists') {
        message = 'A beer with this name already exists for the brewery.';
      } else if (code === 'not-found') {
        message = 'Review or brewery not found.';
      }
      setErrorMessage(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Merge description/ABV from the duplicate into the matched beer (where original is empty)
   * and mark the review as "Updated Original". Calls the reviewDuplicate callable.
   *
   * @param {object} row - The duplicate review row (must have id; matchedBeer.id required by backend for Updated Original).
   * @returns {Promise<{ success: boolean, error?: string }>}
   */
  const copyDescriptionAndCompleteReview = useCallback(async (row) => {
    if (!row?.id) {
      return { success: false, error: 'Invalid review record' };
    }

    if (!row.matchedBeer?.id) {
      return { success: false, error: 'Missing matched beer ID (matchedBeer.id)' };
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const reviewDuplicateFn = httpsCallable(
        functions,
        REVIEW_DUPLICATE_CALLABLE,
        { timeout: REVIEW_DUPLICATE_TIMEOUT_MS },
      );
      await reviewDuplicateFn({
        reviewId: row.id,
        reviewOutcome: 'Updated Original',
      });
      return { success: true };
    } catch (err) {
      console.error('Error copying description and completing review:', err);
      const message = messageForReviewDuplicateError(
        err?.code,
        err?.message,
      );
      setErrorMessage(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Overwrite selected fields on the matched beer from the duplicate candidate.
   * Leaves the review pending (completeReview: false).
   *
   * @param {object} row - Duplicate review row (must have id).
   * @param {string[]} selectedFields - name | abv | style | description
   * @returns {Promise<{ success: boolean, matchedBeer?: object, error?: string }>}
   */
  const applySelectedChanges = useCallback(async (row, selectedFields) => {
    if (!row?.id) {
      return { success: false, error: 'Invalid review record' };
    }
    if (!Array.isArray(selectedFields) || selectedFields.length === 0) {
      return { success: false, error: 'Select at least one field to apply' };
    }

    if (!row.matchedBeer?.id) {
      return { success: false, error: 'Missing matched beer ID (matchedBeer.id)' };
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const reviewDuplicateFn = httpsCallable(
        functions,
        REVIEW_DUPLICATE_CALLABLE,
        { timeout: REVIEW_DUPLICATE_TIMEOUT_MS },
      );
      const result = await reviewDuplicateFn({
        reviewId: row.id,
        reviewOutcome: 'Updated Original',
        selectedFields,
        completeReview: false,
      });
      const data = result?.data ?? {};
      return {
        success: true,
        matchedBeer: data.matchedBeer ?? null,
      };
    } catch (err) {
      console.error('Error applying selected duplicate fields:', err);
      const message = messageForReviewDuplicateError(
        err?.code,
        err?.message,
      );
      setErrorMessage(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    completeReview,
    addBeerAndCompleteReview,
    copyDescriptionAndCompleteReview,
    applySelectedChanges,
    loading,
    errorMessage,
    clearError,
  };
}

export default useBeerDuplicateReviewActions;
